import { useEffect, useMemo, useState } from "react";
import { usePersona } from "../lib/prefs";
import { fetchBrief, type BriefResponse } from "../lib/api";
import { getSessionInfo, type TradingSession } from "../lib/session";
import { PERSONAS } from "@macroscope/shared";

const SESSIONS: { id: TradingSession; label: string }[] = [
  { id: "asia", label: "Asia" },
  { id: "london", label: "London" },
  { id: "new_york", label: "New York" },
];

function useDefaultSession(): TradingSession {
  return useMemo(() => {
    const infos = getSessionInfo();
    const active = infos.find((s) => s.active);
    if (active) return active.id;
    const next = infos.find((s) => s.next);
    return next?.id ?? "london";
  }, []);
}

function BiasPill({ bias }: { bias: "up" | "down" | "neutral" }) {
  const colors =
    bias === "up"
      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
      : bias === "down"
      ? "bg-rose-500/10 text-rose-400 border-rose-500/20"
      : "bg-slate-500/10 text-slate-400 border-slate-500/20";
  return (
    <span className={`rounded border px-2 py-0.5 text-xs font-semibold uppercase tracking-wider ${colors}`}>
      {bias}
    </span>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
        <span>Confidence</span>
        <span>{pct}%</span>
      </div>
      <div className="mt-1 h-1.5 w-full rounded-full bg-surface-2">
        <div
          className="h-1.5 rounded-full bg-macro-accent"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function BriefReads({ reads }: { reads: BriefResponse["reads"] }) {
  if (!reads.length) {
    return (
      <div className="rounded-lg border border-surface-2 bg-surface-1 p-4 text-sm text-slate-400">
        No directional reads available for the selected persona/session.
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {reads.map((read) => (
        <div
          key={read.asset}
          className="rounded-lg border border-surface-2 bg-surface-1 p-4 shadow-sm"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-lg font-semibold text-slate-100">{read.asset}</span>
            <BiasPill bias={read.bias} />
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-300">{read.rationale}</p>
          <ConfidenceBar value={read.confidence} />
          <div className="mt-3 flex flex-wrap gap-1">
            {read.engines.map((engine) => (
              <span
                key={engine}
                className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500"
              >
                {engine}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Watchlist({ items }: { items: BriefResponse["watchlist"] }) {
  if (!items.length) {
    return (
      <div className="rounded-lg border border-surface-2 bg-surface-1 p-4 text-sm text-slate-400">
        No active watchlist items for this session.
      </div>
    );
  }

  return (
    <div className="divide-y divide-surface-2 rounded-lg border border-surface-2 bg-surface-1">
      {items.map((item, idx) => (
        <div
          key={`${item.kind}-${item.label}-${idx}`}
          className="flex items-center justify-between gap-4 px-4 py-3"
        >
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  item.kind === "event"
                    ? "bg-blue-500/10 text-blue-400"
                    : item.kind === "asset"
                    ? "bg-violet-500/10 text-violet-400"
                    : item.kind === "geopolitical"
                    ? "bg-amber-500/10 text-amber-400"
                    : "bg-cyan-500/10 text-cyan-400"
                }`}
              >
                {item.kind}
              </span>
              <span className="font-medium text-slate-200">{item.label}</span>
            </div>
            {item.detail && <p className="mt-0.5 text-xs text-slate-500">{item.detail}</p>}
          </div>
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
              item.urgency === "high"
                ? "bg-rose-500/10 text-rose-400"
                : item.urgency === "medium"
                ? "bg-amber-500/10 text-amber-400"
                : "bg-slate-500/10 text-slate-400"
            }`}
          >
            {item.urgency}
          </span>
        </div>
      ))}
    </div>
  );
}

export function Brief() {
  const [persona, setPersona] = usePersona();
  const defaultSession = useDefaultSession();
  const [session, setSession] = useState<TradingSession>(defaultSession);
  const [brief, setBrief] = useState<BriefResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBrief(persona, session)
      .then((data) => {
        if (!cancelled) setBrief(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [persona, session]);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-slate-100">Session Brief</h2>
          <p className="text-sm text-slate-400">
            A persona-weighted fusion of every engine, refreshed every 15 minutes.
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <select
            value={persona}
            onChange={(e) => setPersona(e.target.value as typeof persona)}
            className="rounded-lg border border-surface-2 bg-surface-1 px-3 py-2 text-sm text-slate-200 outline-none focus:border-macro-accent"
            aria-label="Persona"
          >
            {PERSONAS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
          <div className="flex rounded-lg border border-surface-2 bg-surface-1 p-1">
            {SESSIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setSession(s.id)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  session === s.id
                    ? "bg-macro-accent text-white"
                    : "text-slate-400 hover:text-slate-200"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-500/20 bg-rose-500/10 p-4 text-sm text-rose-200">
          {error}
        </div>
      )}

      {loading && !brief && (
        <div className="rounded-lg border border-surface-2 bg-surface-1 p-8 text-center text-sm text-slate-400">
          Building brief…
        </div>
      )}

      {brief && (
        <div className="space-y-6">
          <div className="rounded-xl border border-surface-2 bg-gradient-to-r from-surface-1 to-surface-2 p-6">
            <h3 className="text-xl font-semibold leading-snug text-slate-100">{brief.headline}</h3>
            <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-400">
              <span className="rounded bg-surface-2 px-2 py-1">{brief.source}</span>
              <span className="rounded bg-surface-2 px-2 py-1">updated {brief.updated_at}</span>
              {brief.delayed && <span className="rounded bg-surface-2 px-2 py-1">delayed</span>}
              {brief.educational && <span className="rounded bg-surface-2 px-2 py-1">educational</span>}
            </div>
          </div>

          {brief.coverage_gaps.length > 0 && (
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-4">
              <h4 className="text-sm font-semibold text-amber-200">Coverage gaps</h4>
              <ul className="mt-1 list-inside list-disc text-xs text-amber-100/80">
                {brief.coverage_gaps.map((gap, i) => (
                  <li key={i}>{gap}</li>
                ))}
              </ul>
            </div>
          )}

          <section className="space-y-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Engine notes</h4>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-lg border border-surface-2 bg-surface-1 p-4">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-macro-accent">Regime</span>
                <p className="mt-1 text-sm text-slate-300">{brief.regime_note}</p>
              </div>
              <div className="rounded-lg border border-surface-2 bg-surface-1 p-4">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-macro-accent">Central banks</span>
                <p className="mt-1 text-sm text-slate-300">{brief.cb_note}</p>
              </div>
              <div className="rounded-lg border border-surface-2 bg-surface-1 p-4">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-macro-accent">Communication</span>
                <p className="mt-1 text-sm text-slate-300">{brief.communication_note}</p>
              </div>
              <div className="rounded-lg border border-surface-2 bg-surface-1 p-4">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-macro-accent">Liquidity</span>
                <p className="mt-1 text-sm text-slate-300">{brief.liquidity_note}</p>
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Model reads</h4>
            <BriefReads reads={brief.reads} />
          </section>

          <section className="space-y-3">
            <h4 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Watchlist</h4>
            <Watchlist items={brief.watchlist} />
          </section>
        </div>
      )}
    </div>
  );
}
